import React, { useState } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import MovdAuthCard from '../design-system/MovdAuthCard.jsx';
import MovdNotice from '../design-system/MovdNotice.jsx';
import MovdInput from '../design-system/MovdInput.jsx';
import MovdButton from '../design-system/MovdButton.jsx';
import { movdTokens } from '../design-system/movdTokens.js';

const AuthPage = ({ onAuth }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [contactDefaults] = useState(() => {
    try {
      const saved = JSON.parse(sessionStorage.getItem('movd.quoteFlow.pending') || '{}');
      const contact = saved.quoteData?.bookingDetails?.contactInfo || {};
      return {
        name: [contact.firstName, contact.lastName].filter(Boolean).join(' '),
        email: contact.email || '',
      };
    } catch {
      return { name: '', email: '' };
    }
  });
  const [name, setName] = useState(contactDefaults.name);
  const [email, setEmail] = useState(contactDefaults.email);
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const getReturnPath = () => {
    const requestedPath = new URLSearchParams(location.search).get('returnTo');
    const storedPath = sessionStorage.getItem('movd.auth.returnTo');
    const returnPath = requestedPath || storedPath;
    return returnPath && returnPath.startsWith('/') && !returnPath.startsWith('//') ? returnPath : '/';
  };

  const switchMode = () => {
    setIsLogin(previous => !previous);
    setMessage(null);
  };

  const resendVerification = async () => {
    setLoading(true);
    try {
      const response = await axios.post('/api/auth/resend-verification', { email });
      setMessage({ severity: 'success', text: response.data.message || 'Verification email sent.' });
    } catch (error) {
      const data = error.response?.data || {};
      setMessage({ severity: 'error', text: data.detail ? `${data.error} (${data.detail})` : data.error || 'Unable to resend verification email.' });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async event => {
    event.preventDefault();
    setLoading(true);
    setMessage(null);
    try {
      const url = isLogin ? '/api/auth/login' : '/api/auth/register';
      const body = isLogin ? { email, password } : { name, email, password };
      const response = await axios.post(url, body);

      if (isLogin && response.data.token) {
        localStorage.setItem('token', response.data.token);
        if (response.data.user) localStorage.setItem('userData', JSON.stringify(response.data.user));
        onAuth?.(response.data.token);
        sessionStorage.removeItem('movd.auth.returnTo');
        navigate(getReturnPath(), { replace: true });
        return;
      }

      if (!isLogin && response.data.success) {
        setIsLogin(true);
        setMessage({ severity: 'success', text: 'Account created. Verify your email, then sign in to continue your quote.', verificationRequired: true });
      }
    } catch (error) {
      const data = error.response?.data || {};
      if (isLogin && data.requiresVerification) {
        setMessage({ severity: 'warning', text: data.error || 'Please verify your email before signing in.', verificationRequired: true });
        return;
      }
      if (!isLogin && data.code === 'ACCOUNT_EXISTS') {
        setIsLogin(true);
        setEmail(email.trim().toLowerCase());
        setMessage({ severity: 'info', text: data.error, accountExists: true });
      } else {
        setMessage({ severity: 'error', text: data.error || 'Authentication failed. Please try again.' });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <MovdAuthCard
      eyebrowAction={<button type="button" onClick={() => navigate(-1)} style={{ background: 'transparent', border: 'none', color: movdTokens.color.content.secondary, font: 'inherit', fontWeight: 700, cursor: 'pointer', marginBottom: 16, padding: 0 }}>‹ Back to your quote</button>}
      title={isLogin ? 'Welcome back' : 'Create your MOVD account'}
      subtitle={isLogin ? 'Sign in to return to your saved quote and finish booking.' : 'Your account keeps your quote, booking updates, payment, and move history together.'}
    >
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: 16, background: movdTokens.color.state.disabledSurface, borderRadius: 10, marginBottom: 20 }}>
        <span aria-hidden="true" style={{ color: movdTokens.color.action.primary }}>🔒</span>
        <div>
          <p style={{ margin: 0, fontWeight: 700, fontSize: 14 }}>Why do we ask you to sign in?</p>
          <p style={{ margin: '4px 0 0', color: movdTokens.color.content.secondary, fontSize: 13, lineHeight: 1.5 }}>To make sure your booking belongs to you, keep your payment connected to the right move, and let you track updates after checkout.</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} style={{ display: 'grid', gap: 16 }}>
        {message && (
          <MovdNotice tone={message.severity}>
            {message.text}
            {message.verificationRequired && <button type="button" disabled={loading} onClick={resendVerification} style={{ display: 'block', marginTop: 8, background: 'transparent', border: 'none', color: 'inherit', fontWeight: 800, textDecoration: 'underline', cursor: 'pointer', padding: 0 }}>Resend email</button>}
            {message.accountExists && <button type="button" onClick={() => setIsLogin(true)} style={{ display: 'block', marginTop: 8, background: 'transparent', border: 'none', color: 'inherit', fontWeight: 800, textDecoration: 'underline', cursor: 'pointer', padding: 0 }}>Use this account</button>}
          </MovdNotice>
        )}

        {!isLogin && <MovdInput label="Full name" value={name} onChange={event => setName(event.target.value)} required autoComplete="name" />}
        <MovdInput label="Email address" type="email" value={email} onChange={event => setEmail(event.target.value)} required autoComplete="email" />
        <MovdInput label="Password" type="password" value={password} onChange={event => setPassword(event.target.value)} required autoComplete={isLogin ? 'current-password' : 'new-password'} />

        <MovdButton type="submit" loading={loading} style={{ width: '100%' }}>
          {isLogin ? 'Sign in and return to quote' : 'Create account'}
        </MovdButton>

        <div style={{ height: 1, background: movdTokens.color.border.subtle, margin: '4px 0' }} />
        <MovdButton type="button" variant="ghost" onClick={switchMode} style={{ width: '100%' }}>
          {isLogin ? 'New to MOVD? Create an account' : 'Already have a MOVD account? Sign in'}
        </MovdButton>
      </form>
    </MovdAuthCard>
  );
};

export default AuthPage;
